import moment from "moment";
import { useIntl } from "react-intl";
import { Col, Row, OverlayTrigger, Tooltip } from "react-bootstrap";

import "../../shipper/ShipmentQuotes/ShipmentQuotes.scss";
import Badge, { BadgeType } from "components/atoms/Badge/Badge";
import CardNew from "components/molecules/Card/CardNew";
import LoadingSkeleton from "components/atoms/LoadingSkeleton/LoadingSkeleton";
import CopyToClipboard from "components/molecules/CopyToClipboard/CopyToClipboard";
import { renderAddressV2InfoForHeaders } from "helpers/addressV2Helpers";
import { getLabelWithTranslationFallback } from "helpers/getLabel";
import { IQuotationHeaderProps } from "./QuotationType";

function QuotationHeader(props: IQuotationHeaderProps) {
  const { data, loadingData } = props || {};

  const intl = useIntl();

  const requestStatus = data?.organization?.request_status;
  const isCanceled = requestStatus === "Canceled" || requestStatus === "Auto canceled";

  const {
    confirmation_date,
    confirmation_time,
    confirmation_date_remaining_time_display,
    confirmation_date_seconds_remaining,
    quoting_deadline_date,
    quoting_deadline_time,
    quoting_deadline_remaining_time_display,
    quoting_deadline_seconds_remaining,
    requestor_email,
    payment_location,
    shipper,
  } = data?.organization || {};
  const {
    pickup_address_name,
    pickup_country_code,
    pickup_state_code,
    pickup_city,
    pickup_zipcode,
    pickup_date,
    pickup_time,
    pickup_end_date,
    pickup_end_time,
    pickup_street,
    pickup_street_line_two,
    stop1_datetime,
    stop1_address_name,
    stop1_country_code,
    stop1_state_code,
    stop1_city,
    stop1_zipcode,
    stop1_date,
    stop1_time,
    stop1_end_date,
    stop1_end_time,
    stop1_street,
    stop1_street_line_two,
    address_v2,
  } = data?.schedule || {};

  const { renderPickupAddress, renderDeliveryAddress } = renderAddressV2InfoForHeaders({ data });

  const header = (title: string) => (
    <p
      className="font-b9"
      style={{
        padding: 0,
        marginBottom: "7px",
      }}
    >
      {title}
    </p>
  );

  const isQuoteRequestBooked = !data?.available_for_booking;
  const getRequestTimerStatusLabel = () => {
    if (isCanceled) {
      if (requestStatus === "Canceled") {
        return intl.formatMessage({
          id: "general.canceled",
          defaultMessage: "Canceled",
        });
      }
      if (requestStatus === "Auto canceled") {
        return intl.formatMessage({
          id: "general.autoCanceled",
          defaultMessage: "Auto-canceled",
        });
      }
    } else if (isQuoteRequestBooked) {
      return intl.formatMessage({
        id: "quote-request.closed",
        defaultMessage: "Closed",
      });
    }
    return "";
  };

  const renderDeadline = (isConfirmationDate = false) => {
    const confirmationDate = `${confirmation_date} ${confirmation_time}`;
    const quotingDeadlineDate = `${quoting_deadline_date} ${quoting_deadline_time}`;
    const deadlineDate = isConfirmationDate ? confirmationDate : quotingDeadlineDate;
    const secondsRemaining = isConfirmationDate ? confirmation_date_seconds_remaining : quoting_deadline_seconds_remaining;
    const timeRemaining = isConfirmationDate
      ? confirmation_date_remaining_time_display
      : quoting_deadline_remaining_time_display;
    let badgeType: BadgeType = "primary";
    if (secondsRemaining > 0 && secondsRemaining < 300) {
      badgeType = "danger";
    } else if (secondsRemaining > 0 && secondsRemaining < 3600) {
      badgeType = "warning";
    }

    const formattedDeadline = moment(new Date(deadlineDate)).format("DD MMM YYYY, HH:mm");
    const formattedConfirmation = moment(new Date(confirmationDate)).format("DD MMM YYYY, HH:mm");

    return (
      <div className="d-flex align-items-center">
        <OverlayTrigger
          placement="bottom"
          delay={{ show: 250, hide: 0 }}
          overlay={<Tooltip id="button-tooltip">{isConfirmationDate ? formattedConfirmation : formattedDeadline}</Tooltip>}
        >
          <div className="position-relative">
            {secondsRemaining < 0 ? (
              <div style={{ whiteSpace: "nowrap" }}>
                <Badge
                  type="danger"
                  label={intl.formatMessage({
                    id: "quote-request.deadlinePassed",
                    defaultMessage: "Deadline has passed",
                  })}
                />
              </div>
            ) : (
              <div style={{ whiteSpace: "nowrap" }}>
                <p className="d-flex">
                  <Badge type={badgeType} label={timeRemaining} />
                </p>
              </div>
            )}
          </div>
        </OverlayTrigger>
      </div>
    );
  };

  if (loadingData)
    return (
      <Row lg={12} className="mx-0">
        <CardNew className="w-100 border-radius-top-0">
          <LoadingSkeleton rows={1} />
        </CardNew>
      </Row>
    );

  return (
    <Row lg={12} className="mx-0">
      <CardNew className="w-100 border-radius-top-0">
        <CardNew.Body>
          <Row>
            <Col className="col-12 mb-3" md>
              {header(intl.formatMessage({ id: "general.createdBy", defaultMessage: "Created By" }))}
              <p
                className="p-0 m-0 cursor-pointer"
                style={{ fontSize: "15px", transform: "translateY(-3px)" }}
                onKeyDown={() => {}}
                onClick={() => {
                  window.open(`mailto:${requestor_email}`);
                }}
              >
                <OverlayTrigger
                  placement="bottom"
                  delay={{ show: 250, hide: 0 }}
                  overlay={<Tooltip id="button-tooltip">{`Mail to: ${requestor_email}`}</Tooltip>}
                >
                  <span>{shipper}</span>
                </OverlayTrigger>
              </p>
              <div className="text-secondary mb-2 font-b2">{payment_location}</div>
            </Col>
            <Col>
              <div className="d-flex">
                <span className="text-capitalize">
                  {header(
                    getLabelWithTranslationFallback("Confirmation time", {
                      translation: intl.formatMessage({
                        id: "new-request.quote-confirmation.schedule.confirmationTime",
                        defaultMessage: "Confirmation time",
                      }),
                      returnAsString: true,
                      withoutInfo: true,
                    }) as string,
                  )}
                </span>
              </div>
              <div className="d-flex">
                {isCanceled || isQuoteRequestBooked ? (
                  <Badge type="secondary" label={getRequestTimerStatusLabel()} />
                ) : (
                  renderDeadline(true)
                )}
              </div>
            </Col>
            <Col>
              <div className="d-flex">
                <span className="text-capitalize">
                  {header(
                    getLabelWithTranslationFallback("Quoting deadline", {
                      translation: intl.formatMessage({
                        id: "new-request.quote-confirmation.schedule.quotingDeadline",
                        defaultMessage: "Quoting deadline",
                      }),
                      returnAsString: true,
                      withoutInfo: true,
                    }) as string,
                  )}
                </span>
              </div>
              <div className="d-flex">
                {isCanceled || isQuoteRequestBooked ? (
                  <Badge type="secondary" label={getRequestTimerStatusLabel()} />
                ) : (
                  renderDeadline()
                )}
              </div>
            </Col>
            <Col>
              {header(
                intl.formatMessage({
                  id: "new-request.schedule.cargoPickup",
                  defaultMessage: "Pickup",
                }),
              )}
              <div
              >
                {Object.keys(address_v2 ?? {}).length > 0 ? (
                  renderPickupAddress()
                ) : (
                  <CopyToClipboard
                    copyOnly
                    text={`${pickup_address_name ? `${pickup_address_name}, ` : ""}
                  ${pickup_street ? `${pickup_street},` : ""}
                  ${pickup_street_line_two ? `${pickup_street_line_two},` : ""}
                  ${pickup_state_code || ""}
                  ${pickup_zipcode ? `${pickup_zipcode}, ` : ""}
                  ${pickup_city ? `${pickup_city}, ` : ""}
                  ${pickup_country_code ? `${pickup_country_code}` : ""}
                  `}
                  >
                    <p className="font-b2">{pickup_address_name}</p>
                    <p className="font-b2">{pickup_street}</p>
                    <p className="font-b2">{pickup_street_line_two}</p>
                    <p className="font-b2">
                      {pickup_state_code || ""} {pickup_zipcode}, {pickup_city}, {pickup_country_code}
                    </p>
                    {pickup_end_date && pickup_end_time ? (
                      <p className="font-b2">
                        {`${moment(pickup_date).format("DD MMM YYYY")} - ${moment(pickup_time, "HH:mm:ss").format(
                          "HH:mm",
                        )} till ${
                          pickup_end_date !== pickup_date ? `${moment(pickup_end_date).format("DD MMM YYYY")} -` : ""
                        } ${moment(pickup_end_time, "HH:mm:ss").format("HH:mm")}`}
                      </p>
                    ) : (
                      <p className="font-b2">
                        {`${moment(pickup_date).format("DD MMM YYYY")} - ${moment(pickup_time, "HH:mm:ss").format("HH:mm")}`}
                      </p>
                    )}
                  </CopyToClipboard>
                )}
              </div>
            </Col>
            <Col>
              {header(
                intl.formatMessage({
                  id: "new-request.schedule.cargoDelivery",
                  defaultMessage: "Delivery",
                }),
              )}

              <div
              >
                {Object.keys(address_v2 ?? {}).length > 0 ? (
                  renderDeliveryAddress()
                ) : (
                  <CopyToClipboard
                    copyOnly
                    text={`${stop1_address_name ? `${stop1_address_name}, ` : ""}
                  ${stop1_street ? `${stop1_street},` : ""} 
                  ${stop1_street_line_two ? `${stop1_street_line_two},` : ""} 
                  ${stop1_state_code ? `${stop1_state_code}` : ""}
                  ${stop1_zipcode ? `${stop1_zipcode}, ` : ""}
                  ${stop1_city ? `${stop1_city}, ` : ""}
                  ${stop1_country_code ? `${stop1_country_code}` : ""}`}
                  >
                    <p className="font-b2">{stop1_address_name}</p>
                    <p className="font-b2">{stop1_street}</p>
                    <p className="font-b2">{stop1_street_line_two}</p>
                    <p className="font-b2">
                      {stop1_state_code || ""} {stop1_zipcode}, {stop1_city}, {stop1_country_code}
                    </p>
                    {stop1_datetime === "ASAP" && <p className="font-b2">ASAP Delivery</p>}
                    {stop1_datetime !== "ASAP" &&
                      (stop1_end_date && stop1_end_time ? (
                        <p className="font-b2">
                          {`${moment(stop1_date).format("MMMM DD, YYYY")} - ${moment(stop1_time, "HH:mm:ss").format(
                            "HH:mm",
                          )} till ${
                            stop1_end_date !== stop1_date ? `${moment(stop1_end_date).format("MMMM DD, YYYY")} -` : ""
                          }
                        ${moment(stop1_end_time, "HH:mm:ss").format("HH:mm")}`}
                        </p>
                      ) : (
                        <p className="font-b2">
                          {`${moment(stop1_date).format("MMMM DD, YYYY")} - ${moment(stop1_time, "HH:mm:ss").format(
                            "HH:mm",
                          )}`}
                        </p>
                      ))}
                  </CopyToClipboard>
                )}
              </div>
            </Col>
          </Row>
        </CardNew.Body>
      </CardNew>
    </Row>
  );
}

export default QuotationHeader;
